<template>
	<div class="mainsearch-wrapper">
		<!-- Fake input visible en permanence : clic = ouvre la modale -->
		<button
			type="button"
			class="mainsearch-fake form-control text-left"
			@click="openModal"
			:aria-label="$t('global.rechercher')"
		>
			<font-awesome-icon :icon="['fal', 'search']" class="mainsearch-fake__icon text-muted"/>
			<span class="text-muted flex-grow-1 mainsearch-fake__label">{{ $t('global.rechercher') }}</span>
			<span class="mainsearch-shortcut mainsearch-fake__shortcut text-muted">
				<kbd>{{ shortcutModifier }} + K</kbd>
			</span>
		</button>

		<b-modal
			ref="searchModal"
			v-model="modalOpen"
			size="xl"
			scrollable
			hide-footer
			hide-header
			body-class="p-0"
			dialog-class="mainsearch-modal-dialog"
			content-class="mainsearch-modal-content"
			@shown="onModalShown"
			@hidden="onModalHidden"
		>
			<div class="mainsearch-layout">
				<!-- 1. Barre de recherche — sticky top -->
				<div class="mainsearch__searchbar">
					<font-awesome-icon :icon="['fal', 'search']" class="mainsearch__search-icon text-muted"/>
					<div class="mainsearch__input-wrapper">
						<input
							ref="input"
							type="search"
							class="mainsearch__input"
							:placeholder="$t('global.rechercher')"
							v-model="search_value"
							@input="onInputChange"
							@keydown="onKeydown"
							@keyup="onKeyup"
							autocomplete="off"
							autocorrect="off"
							autocapitalize="off"
							spellcheck="false"
						/>
						<span v-if="search_value" class="mainsearch__input-hint">
							<kbd>⇧</kbd> <kbd>↵</kbd> {{ $t('advanced_search.search_web') }}
						</span>
					</div>
					<button
						type="button"
						class="mainsearch__close"
						:aria-label="$t('global.fermer')"
						@click="modalOpen = false"
					>
						<font-awesome-icon :icon="['fal', 'times']" />
					</button>
				</div>

				<!-- 2. Zone résultats — flex grow -->
				<div class="mainsearch__results box" ref="results">
					<div v-if="isLoading" class="mainsearch__loading">
						<span class="mainsearch__loading-dot"></span>
						<span class="mainsearch__loading-dot"></span>
						<span class="mainsearch__loading-dot"></span>
					</div>
					<template v-else>
					<!-- Welcome state : champ vide -->
					<div v-if="!search_value" class="mainsearch__welcome">
						<!-- Éléments récemment visités -->
						<div v-if="recentItems.length > 0" class="mainsearch__recent">
							<p class="mainsearch__welcome-label">{{ $t('advanced_search.recently_visited') }}</p>
							<div
								v-for="item in recentItems"
								:key="item.type + '-' + item.id"
								class="mainsearch__option mainsearch__option--recent"
								@mousedown.prevent="searchHorseOrTiers(item)"
								@touchstart.passive="onOptionTouchStart"
								@touchend.prevent="onOptionTouchEnd($event, item)"
							>
								<div v-if="item.type === 'tiers'" class="option__row option__row--tiers">
									<div class="option__col option__col--picto">
										<font-awesome-icon :icon="['fal', 'building']" class="text-muted"/>
									</div>
									<div class="option__col option__col--rs">{{ item.label }}</div>
									<div v-if="item.alias" class="option__col option__col--alias text-muted">{{ item.alias }}</div>
								</div>
								<div v-else-if="item.type === 'horse'" class="option__row option__row--horse">
									<div class="option__col option__col--picto">
										<font-awesome-icon :icon="['fal', 'horse']" class="text-muted"/>
									</div>
									<div class="option__col option__col--horse-name">
										{{ item.label }}<span v-if="item.horse_mort">&nbsp;†</span>
									</div>
									<div v-if="item.alias" class="option__col option__col--horse-alias text-muted">{{ item.alias }}</div>
								</div>
							</div>
							<hr />
						</div>
						<div class="mainsearch__welcome-section">
							<p class="mainsearch__welcome-label">{{ $t('advanced_search.welcome_title') }}</p>
							<div class="mainsearch__welcome-hints">
								<span><kbd>↑</kbd><kbd>↓</kbd> {{ $t('advanced_search.kbd_navigate') }}</span>
								<span><kbd>↵</kbd> {{ $t('advanced_search.kbd_select') }}</span>
								<span><kbd>⇧</kbd><kbd>↵</kbd> {{ $t('advanced_search.search_web') }}</span>
							</div>
							<div class="mainsearch__welcome-fields">
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'horse']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('global.nom') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_alias') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_sire') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_transpondeur') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_interne') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'building']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('tiers.rs') }}</span>
										<span class="mainsearch__tag">{{ $t('tiers.alias') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_lastname') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_firstname') }}</span>
									</span>
								</div>
							</div>

						</div>
						<div class="mainsearch__welcome-section">
							<p class="mainsearch__welcome-label">{{ $t('advanced_search.welcome_advanced_title') }}</p>
							<div class="mainsearch__welcome-hints">{{ $t('advanced_search.welcome_advanced_desc') }}</div>
							<div class="mainsearch__welcome-fields">
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'horse']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('global.nom') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_alias') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_sire') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_transpondeur') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_horse_interne') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'building']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('tiers.rs') }}</span>
										<span class="mainsearch__tag">{{ $t('tiers.alias') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_firstname') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_lastname') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_mail') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_ville') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_telephone') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'user']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_firstname') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_tiers_lastname') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_mail') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_ville') }}</span>
										<span class="mainsearch__tag">{{ $t('advanced_search.field_telephone') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'file-alt']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('global.contracts') }}</span>
										<span class="mainsearch__tag mainsearch__tag--muted">{{ $t('advanced_search.field_num') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'file-invoice']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('horse.factures') }}</span>
										<span class="mainsearch__tag mainsearch__tag--muted">{{ $t('advanced_search.field_num') }}</span>
									</span>
								</div>
								<div class="mainsearch__welcome-fields-row">
									<font-awesome-icon :icon="['fal', 'file-invoice-dollar']" class="mainsearch__welcome-fields-icon"/>
									<span class="mainsearch__welcome-fields-tags">
										<span class="mainsearch__tag">{{ $t('tiers.factures_fournisseurs') }}</span>
										<span class="mainsearch__tag mainsearch__tag--muted">{{ $t('advanced_search.field_num') }}</span>
									</span>
								</div>
							</div>
						</div>
					</div>
						<div
							v-if="search_value && flatResults.length === 0"
							class="mainsearch__empty"
						>
							<font-awesome-icon :icon="['fal', 'search-minus']" class="mainsearch__empty-icon"/>
							<p class="mainsearch__empty-text">{{ $t('global.no_result') }}</p>
							<button type="button" class="mainsearch__empty-link" @click="searchHorseOrTiers({ type: 'more_results' })">
								{{ $t('advanced_search.search_web') }}
								<font-awesome-icon :icon="['fal', 'arrow-right']" class="ml-1"/>
							</button>
						</div>
						<template v-for="(group, gi) in activeGroups">

							<div
								v-for="item in group.values"
								:key="item.type + '-' + item.id"
								class="mainsearch__option"
								:class="{ 'mainsearch__option--active': item.type !== 'more_results' && activeIndex === getFlatIndex(item) }"
								@mousedown.prevent="searchHorseOrTiers(item)"
								@touchstart.passive="onOptionTouchStart"
								@touchend.prevent="onOptionTouchEnd($event, item)"
							>
								<!-- Tiers -->
								<div v-if="item.type === 'tiers'" class="option__row option__row--tiers">
									<div class="option__col option__col--picto">
										<font-awesome-icon :icon="['fal', 'building']" class="text-muted"/>
									</div>
									<div class="option__col option__col--rs">
										<span v-html="highlightMatch(item.label)"></span>
									</div>
									<div v-if="item.alias" class="option__col option__col--alias text-muted">
										<span v-html="highlightMatch(item.alias)"></span>
									</div>
									<small v-if="item.matched_fields && item.matched_fields.length > 0" class="d-block w-100 text-muted option__match">
										<span v-for="(match, idx) in item.matched_fields" :key="idx" class="mr-2">
											<em>{{ $t('advanced_search.field_' + match.field) }}&nbsp;:</em>
											<span v-html="highlightMatch(match.value)"></span>
										</span>
									</small>
								</div>

								<!-- Cheval -->
								<div v-else-if="item.type === 'horse'" class="option__row option__row--horse">
									<div class="option__col option__col--picto">
										<font-awesome-icon :icon="['fal', 'horse']" class="text-muted"/>
									</div>
									<div class="option__col option__col--horse-name">
										<span v-html="highlightMatch(item.label)"></span>
										<span v-if="item.horse_mort">&nbsp;†</span>
										<small v-if="item.matched_fields && item.matched_fields.length > 0" class="d-block w-100 text-muted option__match">
											<span v-for="(match, idx) in item.matched_fields" :key="idx" class="mr-2">
												<em>{{ $t('advanced_search.field_' + match.field) }}&nbsp;:</em>
												<span v-html="highlightMatch(match.value, match.highlight)"></span>
											</span>
										</small>
									</div>
									<div v-if="item.alias" class="option__col option__col--horse-alias text-muted">
										<span v-html="highlightMatch(item.alias)"></span>
									</div>

								</div>

							</div>
							<hr v-if="group.label" :key="'lbl-' + gi" />

						</template>
					</template>
				</div>
			</div>
		</b-modal>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce'
import Constants from '../../../config.local.js'
import Navigation from '@/mixins/Navigation'
import Window from 'GroomyRoot/mixins/Window'

export default {
	mixins: [Navigation, Window],
	data() {
		return {
			options: [
				{ label: this.$t('global.chevaux'), values: [] },
				{ label: this.$t('tiers.tiers'), values: [] },
				{ label: '', values: [] }
			],
			isLoading: false,
			modalOpen: false,
			activeIndex: -1,
			events_tab: {
				'SearchInput::focus': this.onFocus
			},
			debouncedSearch: _debounce(this.search, 300),
			search_value: '',
			recentItems: []
		}
	},
	computed: {
		actualRoute() {
			return this.$route
		},
		shortcutModifier() {
			return this.isMac ? '⌘' : 'Ctrl'
		},
		flatResults() {
			return [
				...this.options[0].values,
				...this.options[1].values
			].filter(r => r.type !== 'more_results')
		},
		activeGroups() {
			return this.options.filter(g => g.values.length > 0)
		}
	},
	watch: {
		$route(to) {
			if (to.name === 'horseFiche' && to.params.horse_id) {
				this.trackVisit('horse', parseInt(to.params.horse_id))
			} else if (to.name === 'tiersFiche' && to.params.tiers_id) {
				this.trackVisit('tiers', parseInt(to.params.tiers_id))
			}
		}
	},
	mounted() {
		window.addEventListener('keydown', this.handleShortcut)
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleShortcut)
	},
	methods: {
		getFlatIndex(item) {
			return this.flatResults.findIndex(r => r.id === item.id && r.type === item.type)
		},
		onInputChange() {
			this.activeIndex = -1
			this.options[0].values = []
			this.options[1].values = []
			this.options[2].values = []
			if (!this.search_value || !this.search_value.trim()) {
				this.debouncedSearch.cancel()
				return
			}
			this.options[2].values = [{
				id: -1,
				type: 'more_results',
				label: this.getTrad('global.more_results')
			}]
			this.debouncedSearch(this.search_value)
		},
		onKeydown(e) {
			const total = this.flatResults.length
			if (e.key === 'ArrowDown') {
				e.preventDefault()
				this.activeIndex = total === 0 ? -1 : (this.activeIndex + 1) % total
				this.scrollActiveIntoView()
			} else if (e.key === 'ArrowUp') {
				e.preventDefault()
				this.activeIndex = total === 0 ? -1 : (this.activeIndex - 1 + total) % total
				this.scrollActiveIntoView()
			} else if (e.key === 'Enter' && e.shiftKey) {
				e.preventDefault()
				if (this.search_value && this.search_value.trim()) {
					this.searchHorseOrTiers({ type: 'more_results' })
				}
			} else if (e.key === 'Enter') {
				e.preventDefault()
				if (this.activeIndex >= 0 && this.activeIndex < total) {
					this.searchHorseOrTiers(this.flatResults[this.activeIndex])
				} else if (this.search_value && this.search_value.trim()) {
					this.searchHorseOrTiers({ type: 'more_results' })
				}
			} else if (e.key === 'Escape') {
				this.modalOpen = false
			}
		},
		// Sur Android, keydown renvoie e.key = 'Unidentified' (IME) — on gère Enter sur keyup
		onKeyup(e) {
			if (e.key === 'Enter' || e.keyCode === 13) {
				const total = this.flatResults.length
				if (e.shiftKey) {
					if (this.search_value && this.search_value.trim()) {
						this.searchHorseOrTiers({ type: 'more_results' })
					}
				} else if (this.activeIndex >= 0 && this.activeIndex < total) {
					this.searchHorseOrTiers(this.flatResults[this.activeIndex])
				} else if (this.search_value && this.search_value.trim()) {
					this.searchHorseOrTiers({ type: 'more_results' })
				}
			}
		},
		scrollActiveIntoView() {
			this.$nextTick(() => {
				const el = this.$el.querySelector('.mainsearch__option--active')
				if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
			})
		},
		onOptionTouchStart(e) {
			const t = e.touches[0]
			this._touchStartX = t.clientX
			this._touchStartY = t.clientY
		},
		onOptionTouchEnd(e, item) {
			if (this._touchStartY !== undefined) {
				const dy = Math.abs(e.changedTouches[0].clientY - this._touchStartY)
				const dx = Math.abs(e.changedTouches[0].clientX - this._touchStartX)
				this._touchStartX = undefined
				this._touchStartY = undefined
				if (dy > 8 || dx > 8) return
			}
			this.searchHorseOrTiers(item)
		},
		async search(search_val) {
			search_val = search_val.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			this.isLoading = true

			const matchesField = (value) => {
				if (!value) return false
				const normalized = value.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
				return new RegExp(this.escapeRegExp(search_val), 'gi').test(normalized)
			}

			// SIRE : 3 à 8 chiffres (numérique seul) ou exactement 8 chiffres + 1 lettre (avec clé)
			const isSireSearch = /^\d{3,8}$/.test(search_val)
			const isSireWithKey = /^\d{8}[A-Za-z]$/.test(search_val)
			const sireQueryStripped = isSireWithKey ? search_val.replace(/[A-Za-z]$/, '') : null
			const matchesSireField = (value) => {
				if (!value || !isSireWithKey) return false
				const normalized = value.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
				return new RegExp(this.escapeRegExp(sireQueryStripped), 'gi').test(normalized)
			}

			// Transpondeur : 3 à 16 chiffres
			const isTranspondeurSearch = /^\d{3,16}$/.test(search_val)

			const horsePromise = this.$storage.db.t('horse').then(table => {
				return table.where({ horse_inactive: 0 }).sortBy('horse_nom')
			}).then(col => col.filter(elem => matchesField(elem.horse_nom) || matchesField(elem.horse_alias) || (isSireSearch && matchesField(elem.horse_sire)) || matchesSireField(elem.horse_sire) || matchesField(elem.horse_interne) || (isTranspondeurSearch && matchesField(elem.horse_transpondeur))))
			.then(arr => arr.slice(0, 20)).then(horses => {
				this.options[0].values = horses.map(horse => {
					const matched_fields = []
					if (horse.horse_alias && matchesField(horse.horse_alias) && horse.horse_alias !== horse.horse_nom) {
						matched_fields.push({ field: 'horse_alias', value: horse.horse_alias })
					}
					if (horse.horse_sire && ((isSireSearch && matchesField(horse.horse_sire)) || matchesSireField(horse.horse_sire))) {
						const sireDisplay = horse.horse_sire + (horse.horse_cle || '')
						const highlight = isSireWithKey ? sireQueryStripped : null
						matched_fields.push({ field: 'horse_sire', value: sireDisplay, highlight })
					}
					if (horse.horse_transpondeur && isTranspondeurSearch && matchesField(horse.horse_transpondeur)) {
						matched_fields.push({ field: 'horse_transpondeur', value: horse.horse_transpondeur })
					}
					if (horse.horse_interne && matchesField(horse.horse_interne)) {
						matched_fields.push({ field: 'horse_interne', value: horse.horse_interne })
					}

					return {
						id: horse.horse_id,
						type: 'horse',
						label: horse.horse_nom,
						alias: horse.horse_alias && horse.horse_alias !== horse.horse_nom ? horse.horse_alias : null,
						horse_mort: horse.horse_mort,
						matched_fields
					}
				})
			})

			let tiersPromise
			if (this.$storage.db.tiers) {
				tiersPromise = this.$storage.db.t('tiers').then(table => {
					return table.where({ tiers_archive: 0 }).sortBy('tiers_rs')
				}).then(col => col.filter(elem => matchesField(elem.tiers_rs) || matchesField(elem.tiers_alias) || matchesField(elem.tiers_lastname) || matchesField(elem.tiers_firstname)))
				.then(arr => arr.slice(0, 20)).then(tiers => {
					const mapped = tiers.map(tier => {
						const matched_fields = []
						if (tier.tiers_lastname && matchesField(tier.tiers_lastname) && !matchesField(tier.tiers_rs)) {
							matched_fields.push({ field: 'tiers_lastname', value: tier.tiers_lastname })
						}
						if (tier.tiers_firstname && matchesField(tier.tiers_firstname) && !matchesField(tier.tiers_rs)) {
							matched_fields.push({ field: 'tiers_firstname', value: tier.tiers_firstname })
						}
						const is_contact = !!(tier.tiers_lastname || tier.tiers_firstname)
						return {
							id: tier.tiers_id,
							type: 'tiers',
							label: tier.tiers_rs,
							alias: tier.tiers_alias && tier.tiers_alias !== tier.tiers_rs ? tier.tiers_alias : null,
							is_contact,
							postalcode: tier.tiers_postalcode || null,
							town: tier.tiers_town || null,
							matched_fields
						}
					})
						this.options[1].values = mapped
				})
			}

			this.options[2].values = [{
				id: -1,
				type: 'more_results',
				label: this.getTrad('global.more_results')
			}]

			await Promise.all([horsePromise, tiersPromise])
			this.isLoading = false
			if (this.flatResults.length > 0) {
				this.activeIndex = 0
			}
		},
		onFocus() {
			this.openModal()
		},
		openModal() {
			this.loadRecentItems()
			if (this.isBelowMd) {
				const fakeInput = document.createElement('input')
				fakeInput.setAttribute('type', 'search')
				fakeInput.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:none;outline:none;'
				document.body.appendChild(fakeInput)
				fakeInput.focus()
				this._fakeInput = fakeInput

				this._scrollY = window.scrollY
				document.body.style.top = `-${this._scrollY}px`
			}
			document.body.classList.add('body--mainsearch-open')
			this.modalOpen = true
		},
		handleShortcut(e) {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
				e.preventDefault()
				this.openModal()
			}
		},
		onModalShown() {
			this.$nextTick(() => {
				if (this._fakeInput) {
					this._fakeInput.remove()
					this._fakeInput = null
				}
				const searchEl = this.$refs.input
				if (!searchEl) return

				if (this.isBelowMd) {
					// touchstart : mémoriser si le doigt est dans les résultats
					this._touchInScrollable = false
					this._touchstartHandler = (e) => {
						this._touchInScrollable = !!e.target.closest('.mainsearch__results')
					}
					// touchmove : bloquer tout sauf la liste
					this._touchmoveHandler = (e) => {
						if (!this._touchInScrollable) e.preventDefault()
					}
					document.addEventListener('touchstart', this._touchstartHandler, { passive: true })
					document.addEventListener('touchmove', this._touchmoveHandler, { passive: false })

					// Scroll (résultats, modale ou window) → blur input pour fermer le clavier iOS
					this._scrollBlurHandler = () => {
						if (document.activeElement === searchEl) searchEl.blur()
					}
					const resultsEl = this.$refs.results
					if (resultsEl) {
						resultsEl.addEventListener('scroll', this._scrollBlurHandler, { passive: true })
						this._resultsEl = resultsEl
					}
					window.addEventListener('scroll', this._scrollBlurHandler, { passive: true })
					const modalEl = document.querySelector('.mainsearch-modal-dialog')
					if (modalEl) {
						modalEl.addEventListener('scroll', this._scrollBlurHandler, { passive: true })
						this._modalScrollEl = modalEl
					}

				}

				searchEl.focus({ preventScroll: true })
			})
		},
		onModalHidden() {
			document.body.classList.remove('body--mainsearch-open')
			if (this.isBelowMd) {
				document.body.style.top = ''
				if (this._scrollY !== undefined) {
					window.scrollTo(0, this._scrollY)
					this._scrollY = undefined
				}
			}
			if (this._touchstartHandler) {
				document.removeEventListener('touchstart', this._touchstartHandler, { passive: true })
				this._touchstartHandler = null
			}
			if (this._touchmoveHandler) {
				document.removeEventListener('touchmove', this._touchmoveHandler, { passive: false })
				this._touchmoveHandler = null
			}
			if (this._scrollBlurHandler) {
				if (this._resultsEl) {
					this._resultsEl.removeEventListener('scroll', this._scrollBlurHandler)
					this._resultsEl = null
				}
				if (this._modalScrollEl) {
					this._modalScrollEl.removeEventListener('scroll', this._scrollBlurHandler)
					this._modalScrollEl = null
				}
				window.removeEventListener('scroll', this._scrollBlurHandler)
				this._scrollBlurHandler = null
			}
			this.options[0].values = []
			this.options[1].values = []
			this.options[2].values = []
			this.search_value = ''
			this.activeIndex = -1
		},
		escapeRegExp(str) {
			return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
		},
		escapeHtml(str) {
			return String(str)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;')
		},
		highlightMatch(value, termOverride) {
			if (value === null || value === undefined || value === '') return ''
			const term = termOverride || (this.search_value || '').trim()
			if (!term) return this.escapeHtml(value)

			const normalizedTerm = term.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			if (!normalizedTerm) return this.escapeHtml(value)

			const original = String(value)
			let normalized = ''
			const indexMap = []
			for (let i = 0; i < original.length; i++) {
				const normChar = original[i].normalize('NFD').replace(/[\u0300-\u036f]/g, '')
				for (let j = 0; j < normChar.length; j++) {
					indexMap.push(i)
				}
				normalized += normChar
			}

			const regex = new RegExp(this.escapeRegExp(normalizedTerm), 'gi')
			const ranges = []
			let match
			while ((match = regex.exec(normalized)) !== null) {
				const startOrig = indexMap[match.index] !== undefined ? indexMap[match.index] : original.length
				const endNormIdx = match.index + match[0].length
				const endOrig = indexMap[endNormIdx] !== undefined ? indexMap[endNormIdx] : original.length
				if (endOrig > startOrig) ranges.push([startOrig, endOrig])
				if (match.index === regex.lastIndex) regex.lastIndex++
			}
			if (ranges.length === 0) return this.escapeHtml(original)

			let result = ''
			let cursor = 0
			ranges.forEach(([start, end]) => {
				result += this.escapeHtml(original.slice(cursor, start))
				result += '<mark>' + this.escapeHtml(original.slice(start, end)) + '</mark>'
				cursor = end
			})
			result += this.escapeHtml(original.slice(cursor))
			return result
		},
		loadRecentItems() {
			const licenceKey = Constants.USER_LICENCE_KEY
			try {
				this.recentItems = JSON.parse(window.localStorage.getItem(`recent_search:${licenceKey}:items`) || '[]')
			} catch (e) {
				this.recentItems = []
			}
		},
		async trackVisit(type, id) {
			this.loadRecentItems()
			if (type === 'horse') {
				const table = await this.$storage.db.t('horse')
				const horse = await table.get(id)
				if (horse) {
					this.saveRecentItem('horse', {
						id: horse.horse_id,
						type: 'horse',
						label: horse.horse_nom,
						alias: horse.horse_alias && horse.horse_alias !== horse.horse_nom ? horse.horse_alias : null,
						horse_mort: horse.horse_mort || false
					})
				}
			} else if (type === 'tiers') {
				const table = await this.$storage.db.t('tiers')
				const tiers = await table.get(id)
				if (tiers) {
					this.saveRecentItem('tiers', {
						id: tiers.tiers_id,
						type: 'tiers',
						label: tiers.tiers_rs,
						alias: tiers.tiers_alias && tiers.tiers_alias !== tiers.tiers_rs ? tiers.tiers_alias : null
					})
				}
			}
		},
		saveRecentItem(type, item) {
			const licenceKey = Constants.USER_LICENCE_KEY
			const storageKey = `recent_search:${licenceKey}:items`
			const filtered = this.recentItems.filter(r => !(r.id === item.id && r.type === item.type))
			filtered.unshift(item)
			this.recentItems = filtered.slice(0, 5)
			window.localStorage.setItem(storageKey, JSON.stringify(this.recentItems))
		},
		async searchHorseOrTiers(option) {
			let routeName = ''
			let params = {}

			if (option.type === 'horse') {
				routeName = 'horseFiche'
				params = { horse_id: option.id }
				this.saveRecentItem('horse', { id: option.id, type: 'horse', label: option.label, alias: option.alias || null, horse_mort: option.horse_mort || false })
			} else if (option.type === 'tiers') {
				routeName = 'tiersFiche'
				params = { tiers_id: option.id }
				this.saveRecentItem('tiers', { id: option.id, type: 'tiers', label: option.label, alias: option.alias || null })
			} else if (option.type === 'more_results') {
				routeName = 'AdvancedSearch'
				params = { search_value: this.search_value }
			}

			this.$store.commit('nav/close')
			if (!this.checkRedirectAuth(routeName, params)) {
				this.modalOpen = false
				return
			}
			this.modalOpen = false
			this.$router.push({ name: routeName, params })
		},
		checkRedirectAuth(routeName, params = {}) {
			let paramsCheck = true
			Object.keys(params).forEach(paramName => {
				if (this.actualRoute.params[paramName] !== params[paramName]) {
					paramsCheck = false
				}
			})
			return !(this.actualRoute.name == routeName && paramsCheck)
		}
	}
}
</script>
